// src/pages/task/views/planning.ts
// Shared, framework-agnostic helpers for the planning views (calendar,
// timeline, schedule). Zero React imports.
//
// Two things live here that the three views used to duplicate badly:
//   * date arithmetic and *locale-driven* formatting (never a hardcoded
//     'en-US' / english weekday table), and
//   * status colours resolved from the loaded TaskStatusDefinitions instead of
//     a per-view hardcoded enum→colour map.

import type { ScheduledWork, Task, TaskStatusDefinition } from '@core/types/task';
import type { Actor } from '@core/types/actor';

import {
  DEFAULT_BOARD_STATUSES,
  DEFAULT_STATUS_ACCENT,
  getTaskCustomStatus,
  getTaskDueDate,
  getTaskStatus,
  taskStatusMap,
} from '@/features/task/logic';

// ---------------------------------------------------------------------------
// Date arithmetic
// ---------------------------------------------------------------------------

export const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Local calendar-day key (`2026-07-27`). Never UTC — the grids are local. */
export function dateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function startOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/** Whole local days from `a` to `b`, ignoring the time of day. */
export function daysBetween(a: Date, b: Date): number {
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / MS_PER_DAY);
}

/** `weekStartsOn`: 0 = Sunday, 1 = Monday. */
export function startOfWeek(date: Date, weekStartsOn = 0): Date {
  const d = startOfDay(date);
  const diff = (d.getDay() - weekStartsOn + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

export function getWeekDays(baseDate: Date, weekStartsOn = 0): Date[] {
  const start = startOfWeek(baseDate, weekStartsOn);
  return Array.from({ length: 7 }, (_, i) => addDays(start, i));
}

/** The 6×7 grid covering `month`, padded with the neighbouring months. */
export function getMonthGrid(year: number, month: number, weekStartsOn = 0): Date[] {
  const first = new Date(year, month, 1);
  const lead = (first.getDay() - weekStartsOn + 7) % 7;
  const start = addDays(first, -lead);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

/** Parse a wire timestamp, returning null instead of an Invalid Date. */
export function parseDate(value: string | undefined | null): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Shift a timestamp by whole days, preserving its time of day. Used for every
 * drag: moving a bar or a chip must not silently rewrite the clock time.
 */
export function shiftIsoByDays(iso: string, days: number): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

/**
 * Move a timestamp onto `target`'s calendar day, keeping its time of day.
 * When there is no source timestamp the day's end is used, which is what a
 * due date on a calendar cell means.
 */
export function moveIsoToDay(iso: string | undefined | null, target: Date): string {
  const source = parseDate(iso);
  const next = new Date(target);
  if (source) {
    next.setHours(source.getHours(), source.getMinutes(), source.getSeconds(), source.getMilliseconds());
  } else {
    next.setHours(23, 59, 59, 0);
  }
  return next.toISOString();
}

// ---------------------------------------------------------------------------
// Locale-driven formatting
// ---------------------------------------------------------------------------

function intl(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat(locale || undefined, options);
  } catch {
    return new Intl.DateTimeFormat(undefined, options);
  }
}

/** Short weekday names in grid order for the active locale. */
export function weekdayNames(locale: string, weekStartsOn = 0): string[] {
  const formatter = intl(locale, { weekday: 'short' });
  // 2023-01-01 was a Sunday, so `+ index` walks the week from Sunday.
  return Array.from({ length: 7 }, (_, i) =>
    formatter.format(new Date(2023, 0, 1 + ((i + weekStartsOn) % 7))),
  );
}

export function formatMonthYear(date: Date, locale: string): string {
  return intl(locale, { month: 'long', year: 'numeric' }).format(date);
}

export function formatShortDate(date: Date, locale: string): string {
  return intl(locale, { month: 'short', day: 'numeric' }).format(date);
}

export function formatWeekdayShort(date: Date, locale: string): string {
  return intl(locale, { weekday: 'short' }).format(date);
}

export function formatDayCellHeader(date: Date, locale: string): string {
  return intl(locale, { weekday: 'short', month: 'short', day: 'numeric' }).format(date);
}

export function formatTime(date: Date, locale: string): string {
  return intl(locale, { hour: '2-digit', minute: '2-digit' }).format(date);
}

/** `9:00 – 11:30`, or just the start when there is no end. */
export function formatTimeRange(start: Date, end: Date | null, locale: string): string {
  const from = formatTime(start, locale);
  return end ? `${from} – ${formatTime(end, locale)}` : from;
}

/** Hours with one decimal at most (`1.5`, `8`). */
export function formatHours(minutes: number): string {
  const hours = minutes / 60;
  return Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
}

// ---------------------------------------------------------------------------
// Scheduled work accessors
// ---------------------------------------------------------------------------

/**
 * List responses carry task fields either flat or nested under `task`
 * (same duality the accessors in features/task/logic.ts handle).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getScheduledWork(item: any): ScheduledWork | null {
  return item?.task?.scheduled_work || item?.scheduled_work || null;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getTaskStartDate(item: any): string | undefined {
  return item?.task?.start_date || item?.start_date;
}

export interface ScheduledRange {
  start: Date;
  end: Date | null;
  isAllDay: boolean;
  timezone: string;
  /** Minutes between start and end; 0 when the block has no end. */
  minutes: number;
}

/** The work block on a task, when it has one. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getScheduledRange(item: any): ScheduledRange | null {
  const work = getScheduledWork(item);
  const start = parseDate(work?.start_time);
  if (!work || !start) return null;
  const end = parseDate(work.end_time);
  return {
    start,
    end,
    isAllDay: !!work.is_all_day,
    timezone: work.timezone || '',
    minutes: end ? Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000)) : 0,
  };
}

/**
 * The day a task belongs on in a planning grid: the start of its scheduled
 * work block when it has one, otherwise its due date. Returns null for tasks
 * that are neither scheduled nor dated — those belong in an "undated" bucket,
 * never on a fabricated day.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getPlannedDate(item: any): Date | null {
  const range = getScheduledRange(item);
  if (range) return range.start;
  return parseDate(getTaskDueDate(item));
}

// ---------------------------------------------------------------------------
// Assignee identity
// ---------------------------------------------------------------------------

export const UNASSIGNED_KEY = '__unassigned__';

/** Stable row key for an assignee (`UNASSIGNED_KEY` when there is none). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getActorKey(actor: any): string {
  return actor?.entity?.id || actor?.id || UNASSIGNED_KEY;
}

/** De-duplicate actors by key, dropping the unassigned bucket. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function collectActors(items: any[]): Actor[] {
  const byKey = new Map<string, Actor>();
  for (const item of items) {
    const actor = item?.task?.assignee || item?.assignee;
    const key = getActorKey(actor);
    if (key === UNASSIGNED_KEY || byKey.has(key)) continue;
    byKey.set(key, actor as Actor);
  }
  return Array.from(byKey.values());
}

// ---------------------------------------------------------------------------
// Status colours from TaskStatusDefinitions
// ---------------------------------------------------------------------------

export interface StatusStyle {
  /** Tailwind background class; empty when `hex` carries the colour. */
  className: string;
  /** Colour configured on a status definition; empty when `className` applies. */
  hex: string;
  /** Display name from a definition; empty when `labelKey` applies. */
  label: string;
  /** i18n key for base statuses; empty when `label` applies. */
  labelKey: string;
}

const FALLBACK_STYLE: StatusStyle = {
  className: 'bg-muted-foreground',
  hex: '',
  label: '',
  labelKey: 'common.unknown',
};

function styleFromDefinition(def: TaskStatusDefinition): StatusStyle {
  return {
    className: def.color ? '' : DEFAULT_STATUS_ACCENT[String(def.category ?? '')] || FALLBACK_STYLE.className,
    hex: def.color || '',
    label: def.display_name || def.slug,
    labelKey: '',
  };
}

function styleFromBaseStatus(status: string): StatusStyle {
  const accent = DEFAULT_STATUS_ACCENT[status];
  const mapping = taskStatusMap[status];
  if (!accent && !mapping) return FALLBACK_STYLE;
  return {
    className: accent || FALLBACK_STYLE.className,
    hex: '',
    label: '',
    labelKey: mapping?.labelKey || FALLBACK_STYLE.labelKey,
  };
}

/**
 * Resolve the colour of a task from the status definitions loaded for the
 * board. A task matches a definition by `custom_status` slug first, then by
 * the definition whose `category` equals its base status. Without definitions
 * (or without a match) the shared base-category accent is used.
 */
export function resolveStatusStyle(
  task: Task,
  definitions: TaskStatusDefinition[] = [],
): StatusStyle {
  const slug = getTaskCustomStatus(task);
  const base = getTaskStatus(task);

  if (definitions.length > 0) {
    const bySlug = slug ? definitions.find((def) => def.slug === slug) : undefined;
    if (bySlug) return styleFromDefinition(bySlug);
    const byCategory = base
      ? definitions.find((def) => String(def.category ?? '') === base)
      : undefined;
    if (byCategory) return styleFromDefinition(byCategory);
  }

  return base ? styleFromBaseStatus(base) : FALLBACK_STYLE;
}

export interface LegendEntry extends StatusStyle {
  id: string;
}

/** Legend rows for the timeline: the configured statuses, or the base ones. */
export function buildStatusLegend(definitions: TaskStatusDefinition[] = []): LegendEntry[] {
  if (definitions.length > 0) {
    return [...definitions]
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      .slice(0, 8)
      .map((def) => ({ id: def.slug, ...styleFromDefinition(def) }));
  }
  return DEFAULT_BOARD_STATUSES.map((status) => ({ id: status, ...styleFromBaseStatus(status) }));
}

// ---------------------------------------------------------------------------
// Capacity (availability slots ∩ one day)
// ---------------------------------------------------------------------------

export interface TimeSlotLike {
  start_time?: string;
  end_time?: string;
}

/** Minutes of `slots` that fall inside `day`. */
export function minutesInDay(slots: TimeSlotLike[] | undefined, day: Date): number {
  if (!slots?.length) return 0;
  const from = startOfDay(day).getTime();
  const to = from + MS_PER_DAY;
  let total = 0;
  for (const slot of slots) {
    const start = parseDate(slot.start_time);
    const end = parseDate(slot.end_time);
    if (!start || !end) continue;
    const overlap = Math.min(end.getTime(), to) - Math.max(start.getTime(), from);
    if (overlap > 0) total += overlap / 60000;
  }
  return Math.round(total);
}

/** Minutes of `slots` inside the whole range (used for weekly totals). */
export function minutesInRange(
  slots: TimeSlotLike[] | undefined,
  from: Date,
  to: Date,
): number {
  if (!slots?.length) return 0;
  const start = from.getTime();
  const end = to.getTime();
  let total = 0;
  for (const slot of slots) {
    const slotStart = parseDate(slot.start_time);
    const slotEnd = parseDate(slot.end_time);
    if (!slotStart || !slotEnd) continue;
    const overlap = Math.min(slotEnd.getTime(), end) - Math.max(slotStart.getTime(), start);
    if (overlap > 0) total += overlap / 60000;
  }
  return Math.round(total);
}
